import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../WorkflowTask.scss';
import { useDispatch } from 'react-redux'
import HrbTranslate from '../../../../../components/language/HrbTranslate'
import Lang from '../../../../../components/language/services/Lang'
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask'
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import WorkflowTaskStep from '../../../../../components/library/workflow-tasks/WorkflowTaskStep';
import Button from '../../../../../components/library/buttons/Button';
import { TaskStatus } from '../../../services/TaskStatus';
import DocumentReviewService from '../../../services/documentReview/DocumentReviewService';
import TicketActions from '../../../../../store/ticketRedux';
import { createAuthorizationMethod, createDocumentReviewData } from '../../../services/documentReview/DocumentReviewUtils';
import { useWorkflowState } from '../../hooks/useWorkflowStateHook';
import { useTicketState } from '../../hooks/useTicketStateHook';
import DoxExplorer from '../../dox/Explorer/doxExplorer';
import Checkbox from 'components/library/buttons/Checkbox';
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook';
import SectionDivider from 'components/library/common/SectionDivider';
import DocumentReviewAuthorizationTask from './DocumentReviewAuthorizationTask';

const DocumentReviewRteTask = (props) => {
    const ticketIsOpen = useTicketState(props.ticket);
    const [returnType,,, primaryName, spouseName] = useInterviewInfo(props.ticket);
    const [baseStatus, completions] = useTaskStatus(props.ticket, [WorkflowTasks.DOCUMENT_REVIEW]);
    const isCoupled = returnType === 'coupled';

    const populateDocumentReviewTaskData = () => {
        var primaryAuthorizationMethod = createAuthorizationMethod(null);
        var spouseAuthorizationMethod = isCoupled ? createAuthorizationMethod(null) : null;
        return createDocumentReviewData(primaryAuthorizationMethod, spouseAuthorizationMethod, false, false, false);
    }

    const [documentReviewTaskData, setDocumentReviewTaskData] = useState(populateDocumentReviewTaskData);

    const getDocumentReviewTaskData = () => {
        if(completions.length <= 0) {
            return;
        }
        
        var documentReviewTask =  JSON.parse(completions[0].completionData);
        if(!documentReviewTask) {
            return;
        }

        setDocumentReviewTaskData(documentReviewTask);
    }
    const stateTasks = useWorkflowState(props.ticket);
    const dispatch = useDispatch();
    const [explorerShowing, setExplorerShowing] = useState(false);    
    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);

    // State Variables
    const [isDocumentReviewInProgress, setIsDocumentReviewInProgress] = useState(false);
    const [isDocumentReviewComplete, setIsDocumentReviewComplete] = useState(false);
    const [isPrimaryAuthorizationConfirmed, setIsPrimaryAuthorizationConfirmed] = useState(false);
    const [isSpouseAuthorizationConfirmed, setIsSpouseAuthorizationConfirmed] = useState(false);
    const [hasUserAuthorizationChanged, setHasUserAuthorizationChanged] = useState(false);
    const [hasReviewedDocuments, setHasReviewedDocuments] = useState(false);
    const [hasContactedClient, setHasContactedClient] = useState(false);
    const [isReadyToPrepareReturn, setIsReadyToPrepareReturn] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isDocumentReviewButtonEnabled, setIsDocumentReviewButtonEnabled] = useState(false);

    const updateDocumentReviewState = () => {
        if(!documentReviewTaskData) {
            return;
        }
        setHasReviewedDocuments(documentReviewTaskData.hasReviewedDocuments);
        setHasContactedClient(documentReviewTaskData.hasContactedClient);
        setIsReadyToPrepareReturn(documentReviewTaskData.isReadyToPrepareReturn);
    }

    useEffect(() => {
        getDocumentReviewTaskData();        
        var isDocumentReviewTask = (stateTasks || []).find(st => st.identifier === WorkflowTasks.DOCUMENT_REVIEW);
        if(!isDocumentReviewTask) {
            setIsDocumentReviewInProgress(false);
            if(baseStatus === TaskStatus.COMPLETE) {
                setIsDocumentReviewComplete(true);
                updateDocumentReviewState();
            } else if(baseStatus === TaskStatus.NOT_STARTED && completions.length === 0) {
                setIsDocumentReviewComplete(false);
                setHasReviewedDocuments(false);
                setHasContactedClient(false);
                setIsReadyToPrepareReturn(false);
                setDocumentReviewTaskData(populateDocumentReviewTaskData());
                setIsPrimaryAuthorizationConfirmed(false);
                setIsSpouseAuthorizationConfirmed(false);
            }
            setStatus(baseStatus);
            return;
        }
        if(baseStatus !== TaskStatus.COMPLETE) {
            setIsDocumentReviewInProgress(true);
            setIsDocumentReviewComplete(false);
            setStatus(TaskStatus.IN_PROGRESS);
            updateDocumentReviewState();
        }
    }, [baseStatus, stateTasks])

    useEffect(() => {
        setDocumentReviewButton();
        if (documentReviewTaskData.hasReviewedDocuments !== hasReviewedDocuments) {
            documentReviewTaskData.hasReviewedDocuments = hasReviewedDocuments;
        }
        if (documentReviewTaskData.hasContactedClient !== hasContactedClient) {
            documentReviewTaskData.hasContactedClient = hasContactedClient;
        }
        if (documentReviewTaskData.isReadyToPrepareReturn !== isReadyToPrepareReturn) {
            documentReviewTaskData.isReadyToPrepareReturn = isReadyToPrepareReturn;
        }
        saveDocumentReviewData();
    }, [hasUserAuthorizationChanged, hasReviewedDocuments, hasContactedClient, isReadyToPrepareReturn]);

    useEffect(() => {
        setDocumentReviewButton();
    }, [isDocumentReviewInProgress, isPrimaryAuthorizationConfirmed, isSpouseAuthorizationConfirmed]);

    const setDocumentReviewButton = () => {
        if(!isDocumentReviewInProgress) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        if(!isPrimaryAuthorizationConfirmed || (isCoupled && !isSpouseAuthorizationConfirmed)) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        if(isCoupled && !isSpouseAuthorizationConfirmed) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        if(!hasReviewedDocuments) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        if(!hasContactedClient) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        if(!isReadyToPrepareReturn) {
            setIsDocumentReviewButtonEnabled(false);
            return;
        }
        setIsDocumentReviewButtonEnabled(true);  
    }

    const toggleHasReviewedDocuments = (_) => {
        setIsCompleting(true);
        setHasReviewedDocuments(!hasReviewedDocuments);
    };

    const toggleHasContactedClient = (_) => {
        setIsCompleting(true);
        setHasContactedClient(!hasContactedClient);
    };
    
    const toggleIsReadyToPrepareReturn = (_) => {
        setIsReadyToPrepareReturn(!isReadyToPrepareReturn);
    };

    const saveUserAuthorizationData = async (filer, authorizationData) => {
        setIsCompleting(true);
        setHasUserAuthorizationChanged(prev => !prev);
        if(filer == 'primary') {
            documentReviewTaskData.primary = authorizationData;
        }
        else if(isCoupled && filer == 'spouse') {
            documentReviewTaskData.spouse = authorizationData;
        }
    }

    const saveDocumentReviewData = async () => {
        if(!isCompleting) return;
        await DocumentReviewService.updateDocumentReviewTask(props.ticket.id, documentReviewTaskData);
        setIsCompleting(false);
    }

    const handleReviewAction = async () => {
        setIsCompleting(true);
        let ticketId = await DocumentReviewService.completeDocumentReviewTask(props.ticket.id, documentReviewTaskData);
        if (ticketId !== 0) {
            dispatch(TicketActions.getTicket(ticketId));
        }
        setIsCompleting(false);
    }

    const createLink = (urlKey, titleKey) => (
        <a href={Lang.getCurrentValue(urlKey)} target='_blank' rel='noreferrer noopener'>
            <HrbTranslate text={titleKey} />
        </a>
    )

    const updateAuthorizationStatus = (filer, authorizationStatus) => {
        if(filer === 'primary' && authorizationStatus !== isPrimaryAuthorizationConfirmed) {
            setIsPrimaryAuthorizationConfirmed(authorizationStatus);
        }
        else if(isCoupled && filer === 'spouse' && authorizationStatus !== isSpouseAuthorizationConfirmed) {
            setIsSpouseAuthorizationConfirmed(authorizationStatus);
        }
    }

    const resetSignaturePage = async (workflowTask) => {
        if(await DocumentReviewService.resetSignaturePage(props.ticket.id, workflowTask)) {
            dispatch(TicketActions.getTicket(props.ticket.id));
        }
    }

    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.DOCUMENT_REVIEW} ticket={props.ticket}>
            <div className='workflow-task-content'>
                <WorkflowTaskStep
                    helpKey='0ProSupport/VDOPro/DocumentReview.htm'
                    title={'DocumentReviewTask.ConfirmAuthorizationTaskStep'}
                    titleClassNames='p1 medium-weight' />
                <p className='p2'><HrbTranslate text='DocumentReviewTask.ConfirmAuthorizationTaskStepDescription' params={{
                    WarmWelcomeCall: createLink('DocumentReviewTask.WarmWelcomeCallLink', 'DocumentReviewTask.WarmWelcomeCall')
                }} /></p>
                <div className='accordion-section'>
                    {<DocumentReviewAuthorizationTask elementId='document-review-did-authorization-pass-accordion-primary'
                        title={ !isCoupled ? 'DocumentReviewTask.DidAuthorizationPassLabel' : primaryName }
                        translateTitle={!isCoupled}
                        groupLabel = { isCoupled ? 'DocumentReviewTask.DidAuthorizationPassLabel' : null }
                        isInProgress={isDocumentReviewInProgress}
                        filer='primary'
                        filerName={primaryName}
                        authorizationInfo={documentReviewTaskData?.primary}
                        saveAuthorizationData={v => saveUserAuthorizationData('primary', v)}
                        completeAuthorization={v => updateAuthorizationStatus('primary', v)}
                        ticket={props.ticket}
                        signaturePageWorkflowTask={WorkflowTasks.SIGNATUREPAGE_P} 
                        resetSignaturePage={v => resetSignaturePage(WorkflowTasks.SIGNATUREPAGE_P)} />}
                    {isCoupled && spouseName && <DocumentReviewAuthorizationTask elementId='document-review-did-authorization-pass-accordion-spouse'
                        title={ spouseName }
                        groupLabel = 'DocumentReviewTask.DidAuthorizationPassLabel'
                        translateTitle={false}
                        isInProgress={isDocumentReviewInProgress}
                        filer='spouse'
                        filerName={spouseName}
                        authorizationInfo={documentReviewTaskData?.spouse}
                        saveAuthorizationData={v => saveUserAuthorizationData('spouse', v)} 
                        completeAuthorization={v => updateAuthorizationStatus('spouse', v)} 
                        ticket={props.ticket}
                        signaturePageWorkflowTask={WorkflowTasks.SIGNATUREPAGE_S} 
                        resetSignaturePage={v => resetSignaturePage(WorkflowTasks.SIGNATUREPAGE_S)} />}
                </div>                
                <SectionDivider /> 
                <p className='p1 medium-weight'><HrbTranslate text='DocumentReviewTask.ReviewAndContactClientTaskStep' /></p>
                <Checkbox textKey={`DocumentReviewTask.ReviewClientDocumentsAndNotesCheckBox`}
                    checked={hasReviewedDocuments}
                    onChecked={toggleHasReviewedDocuments}
                    elementId={'document-review-client-documents-notes-checkbox'}
                    disabled={!isDocumentReviewInProgress} />
                <Checkbox textKey={`DocumentReviewTask.CallClientCheckBox`}
                    checked={hasContactedClient}
                    onChecked={toggleHasContactedClient}
                    elementId={'document-review-call-client-checkbox'}
                    disabled={!isDocumentReviewInProgress} 
                    params={{
                        DocumentReviewCallScript: createLink('DocumentReviewTask.DocumentReviewCallScriptLink', 'DocumentReviewTask.DocumentReviewCallScript')
                    }} />
                <p className='p2'><HrbTranslate text='DocumentReviewTask.AuthorizationRequestNote' /></p>
                <Button
                    elementId='document-review-manage-btn'
                    labelKey='DocUploadTask.ViewManage'
                    buttonStyles='secondary center'
                    onClick={() => setExplorerShowing(true)}
                    disabled={isDocumentReviewComplete || !ticketIsOpen} />
                <SectionDivider /> 
                <p className='p1 medium-weight'><HrbTranslate text='DocumentReviewTask.ProvideClientQuoteTaskStep' /></p>
                <div>
                    <p className='p2'><HrbTranslate text='DocumentReviewTask.PrepareQuoteUsingUpfrontPricingCalculator' 
                                                    params={{ UpfrontPricingCalculator: createLink('DocumentReviewTask.UpfrontPricingCalculatorLink', 'DocumentReviewTask.UpfrontPricingCalculator')}} />
                    
                    </p>
                    <p className='p2'><HrbTranslate text='DocumentReviewTask.SendClientQuote' 
                                                    params={{ ThankClientAndProvideQuote: createLink('DocumentReviewTask.ThankClientAndProvideQuoteLink', 'DocumentReviewTask.ThankClientAndProvideQuote')}} />
                    </p>
                </div>
                <Checkbox textKey={`DocumentReviewTask.ReadyToPrepareReturnCheckbox`}
                    checked={isReadyToPrepareReturn}
                    onChecked={toggleIsReadyToPrepareReturn}
                    elementId={'document-review-ready-to-prepare-checkbox'}
                    disabled={!isDocumentReviewInProgress} />
                <Button
                    elementId='document-review-done-btn'
                    labelKey='DocumentReviewTask.Complete'
                    buttonStyles='primary center'
                    onClick={handleReviewAction}
                    disabled={!isDocumentReviewButtonEnabled}
                    loading={isCompleting && isDocumentReviewButtonEnabled}
                />
                <p className='p2'><HrbTranslate text='DocumentReviewTask.DocumentReviewCompletionNote' /></p>
            </div>
            <DoxExplorer ticket={props.ticket} show={explorerShowing}
                onClose={() => setExplorerShowing(false)}
            />            
        </WorkflowTask>
    );
}

DocumentReviewRteTask.whyDidYouRender = false;

DocumentReviewRteTask.propTypes = {
    ticket: PropTypes.object,
}


export default DocumentReviewRteTask